import { lazy, Suspense, useEffect } from 'react'
import { Routes, Route, Navigate } from 'react-router-dom'
import { useThemeEffect } from '@/hooks/useThemeEffect'
import { MainLayout } from '@/components/layout/MainLayout'
import { ProtectedRoute, GuestRoute } from '@/components/auth/ProtectedRoute'
import { LoginPage } from '@/pages/LoginPage'
import { DashboardPage } from '@/pages/DashboardPage'
import { ClientsPage } from '@/pages/ClientsPage'
import { ClientDashboardPage } from '@/pages/ClientDashboardPage'
import { CalendarPage } from '@/pages/CalendarPage'
import { ProjectsPage } from '@/pages/ProjectsPage'
import { ProjectDetailPage } from '@/pages/ProjectDetailPage'
import { TasksPage } from '@/pages/TasksPage'
import { PEDPage } from '@/pages/PEDPage'
import { SharedPEDPage } from '@/pages/SharedPEDPage'
import { ADVPage } from '@/pages/ADVPage'
import { AIAssistantPage } from '@/pages/AIAssistantPage'
import { SettingsPage } from '@/pages/SettingsPage'
import { UsersAdminPage } from '@/pages/UsersAdminPage'
import { ReportsPage } from '@/pages/ReportsPage'
import { AuditTrailPage } from '@/pages/AuditTrailPage'
import { NotificationsPage } from '@/pages/NotificationsPage'
import { useInstanceStore, isInstanceComplete, firstModulePath } from '@/stores/instanceStore'

const GuestsAdminPage = lazy(() =>
  import('./pages/GuestsAdminPage').then((m) => ({ default: m.GuestsAdminPage }))
)
const AIHealthPage = lazy(() =>
  import('./pages/AIHealthPage').then((m) => ({ default: m.AIHealthPage }))
)
const TelegramLinkPage = lazy(() =>
  import('./pages/TelegramLinkPage').then((m) => ({ default: m.TelegramLinkPage }))
)

const PRPage = lazy(() => import('./pages/PRPage').then((m) => ({ default: m.PRPage })))
const BrandingPage = lazy(() =>
  import('./pages/BrandingPage').then((m) => ({ default: m.BrandingPage }))
)
const ConsulenzaPage = lazy(() =>
  import('./pages/ConsulenzaPage').then((m) => ({ default: m.ConsulenzaPage }))
)
const DigitalHubPage = lazy(() =>
  import('./pages/DigitalHubPage').then((m) => ({ default: m.DigitalHubPage }))
)
const AccountingPage = lazy(() =>
  import('./pages/AccountingPage').then((m) => ({ default: m.AccountingPage }))
)
const QuotesPage = lazy(() =>
  import('./pages/QuotesPage').then((m) => ({ default: m.QuotesPage }))
)
const QuoteDetailPage = lazy(() =>
  import('./pages/QuoteDetailPage').then((m) => ({ default: m.QuoteDetailPage }))
)
const TeamWorkloadPage = lazy(() =>
  import('./pages/TeamWorkloadPage').then((m) => ({ default: m.TeamWorkloadPage }))
)
const PersonalCalendarPage = lazy(() =>
  import('./pages/PersonalCalendarPage').then((m) => ({ default: m.PersonalCalendarPage }))
)
const CatalogAdminPage = lazy(() =>
  import('./pages/CatalogAdminPage').then((m) => ({ default: m.CatalogAdminPage }))
)
const SuppliersPage = lazy(() =>
  import('./pages/SuppliersPage').then((m) => ({ default: m.SuppliersPage }))
)
const KnowledgeBasePage = lazy(() =>
  import('./pages/KnowledgeBasePage').then((m) => ({ default: m.KnowledgeBasePage }))
)
const EmailPage = lazy(() =>
  import('./pages/EmailPage').then((m) => ({ default: m.EmailPage }))
)
const RisorsePage = lazy(() =>
  import('./pages/RisorsePage').then((m) => ({ default: m.RisorsePage }))
)
const SocialPublisherPage = lazy(() =>
  import('./pages/SocialPublisherPage').then((m) => ({ default: m.SocialPublisherPage }))
)
const RichiestePage = lazy(() => import('./pages/RichiestePage'))
const LeadDetailPage = lazy(() => import('./pages/LeadDetailPage'))
const VisualProductionPage = lazy(() =>
  import('./pages/VisualProductionPage').then((m) => ({ default: m.VisualProductionPage }))
)
const MerchandisingPage = lazy(() =>
  import('./pages/MerchandisingPage').then((m) => ({ default: m.MerchandisingPage }))
)
const PendingChangesPage = lazy(() =>
  import('./pages/PendingChangesPage').then((m) => ({ default: m.PendingChangesPage }))
)
const ContentReviewPage = lazy(() =>
  import('./pages/ContentReviewPage').then((m) => ({ default: m.ContentReviewPage }))
)
const NotesPage = lazy(() =>
  import('./pages/NotesPage').then((m) => ({ default: m.NotesPage }))
)
const TodoPage = lazy(() =>
  import('./pages/TodoPage').then((m) => ({ default: m.TodoPage }))
)
const CrescitaSocialPage = lazy(() =>
  import('./pages/CrescitaSocialPage').then((m) => ({ default: m.CrescitaSocialPage }))
)
const WarehousePage = lazy(() =>
  import('./pages/WarehousePage').then((m) => ({ default: m.WarehousePage }))
)
const EquipmentPublicPage = lazy(() =>
  import('./pages/EquipmentPublicPage').then((m) => ({ default: m.EquipmentPublicPage }))
)
const NegozioCommentiPage = lazy(() =>
  import('./pages/NegozioCommentiPage').then((m) => ({ default: m.NegozioCommentiPage }))
)
const PartnerLoginPage = lazy(() => import('./pages/partner/PartnerLoginPage'))
const PartnerLayout = lazy(() => import('./components/partner/PartnerLayout'))
const PartnerDashboardPage = lazy(() => import('./pages/partner/PartnerDashboardPage'))
const PartnerCreateQuotePage = lazy(() => import('./pages/partner/PartnerCreateQuotePage'))
const PartnerQuoteDetailPage = lazy(() => import('./pages/partner/PartnerQuoteDetailPage'))
const PartnerAccountPage = lazy(() => import('./pages/partner/PartnerAccountPage'))
const PartnerCollaboratorsPage = lazy(() => import('./pages/PartnerCollaboratorsPage'))

function PageLoader() {
  return (
    <div className="flex items-center justify-center min-h-64">
      <div className="w-6 h-6 border-2 border-primary-300 border-t-primary-700 rounded-full animate-spin" />
    </div>
  )
}

// Con istanza non completa (es. seconda istanza PROXIMA con solo Contabilità
// ed Email, vedi stores/instanceStore.ts) le pagine dei moduli spenti non
// sono navigabili: chi arriva lì (link vecchio, URL digitato a mano) viene
// rimandato al primo modulo attivo. Le API restano raggiungibili: qui si
// nasconde solo la navigazione, come richiesto.
function Gate({ allowed, children }: { allowed: boolean; children: React.ReactNode }) {
  const target = useInstanceStore((s) => firstModulePath(s.instance))
  if (!allowed) return <Navigate to={target} replace />
  return <>{children}</>
}

// Componente principale con struttura delle route
export default function App() {
  // Applica il tema (light/dark/system) all'avvio e ad ogni cambio
  useThemeEffect()

  // Istanza completa (comportamento di sempre) o seconda istanza con solo
  // alcuni moduli attivi (es. PROXIMA → solo Contabilità ed Email)
  const instance = useInstanceStore((s) => s.instance)
  const isComplete = isInstanceComplete(instance)

  // Titolo della scheda: nome dell'istanza al posto di "E-quipe ERP" quando non è completa
  useEffect(() => {
    document.title = isComplete ? 'E-quipe ERP' : instance.name
  }, [isComplete, instance.name])

  return (
    <Routes>
      {/* Route pubblica - Login */}
      <Route path="/login" element={<LoginPage />} />

      {/* Route pubblica - PED condiviso */}
      <Route path="/shared/ped/:token" element={<SharedPEDPage />} />

      {/* Route pubblica - Equipment QR scan */}
      <Route
        path="/equipment/:id"
        element={
          <Suspense fallback={<PageLoader />}>
            <EquipmentPublicPage />
          </Suspense>
        }
      />

      {/* Route pubblica - Negozio Commenti, il cliente ordina commenti IG/FB col suo credito */}
      <Route
        path="/shop/commenti/:token"
        element={
          <Suspense fallback={<PageLoader />}>
            <NegozioCommentiPage />
          </Suspense>
        }
      />

      {/* Area Partner / Collaboratori esterni — auth indipendente */}
      <Route path="/partner/login" element={<Suspense fallback={<PageLoader />}><PartnerLoginPage /></Suspense>} />
      <Route path="/partner" element={<Suspense fallback={<PageLoader />}><PartnerLayout /></Suspense>}>
        <Route index element={<Suspense fallback={<PageLoader />}><PartnerDashboardPage /></Suspense>} />
        <Route path="nuovo" element={<Suspense fallback={<PageLoader />}><PartnerCreateQuotePage /></Suspense>} />
        <Route path="preventivi/:id" element={<Suspense fallback={<PageLoader />}><PartnerQuoteDetailPage /></Suspense>} />
        <Route path="account" element={<Suspense fallback={<PageLoader />}><PartnerAccountPage /></Suspense>} />
      </Route>

      {/* Route protette - richiede autenticazione */}
      <Route
        path="/"
        element={
          <ProtectedRoute>
            <MainLayout />
          </ProtectedRoute>
        }
      >
        {/* Dashboard principale — con istanza non completa va sul primo modulo attivo */}
        <Route index element={<Gate allowed={isComplete}><GuestRoute><DashboardPage /></GuestRoute></Gate>} />

        {/* Gestione clienti (L3+) */}
        <Route path="clients" element={<Gate allowed={isComplete}><ProtectedRoute maxLevel={3}><GuestRoute><ClientsPage /></GuestRoute></ProtectedRoute></Gate>} />
        <Route path="clients/:id" element={<Gate allowed={isComplete}><ProtectedRoute maxLevel={3}><GuestRoute><ClientDashboardPage /></GuestRoute></ProtectedRoute></Gate>} />

        {/* Calendario personale — call, appuntamenti, meeting */}
        <Route
          path="calendar"
          element={
            <Gate allowed={isComplete}>
              <GuestRoute>
                <Suspense fallback={<PageLoader />}>
                  <PersonalCalendarPage />
                </Suspense>
              </GuestRoute>
            </Gate>
          }
        />

        {/* Calendario pubblicazioni — contenuti social, scadenze */}
        <Route path="calendario-pubblicazioni" element={<Gate allowed={isComplete}><GuestRoute><CalendarPage /></GuestRoute></Gate>} />

        {/* Gestione progetti */}
        <Route path="projects" element={<Gate allowed={isComplete}><ProjectsPage /></Gate>} />
        <Route path="projects/:id" element={<Gate allowed={isComplete}><ProjectDetailPage /></Gate>} />

        {/* Gestione task */}
        <Route path="tasks" element={<Gate allowed={isComplete}><GuestRoute><TasksPage /></GuestRoute></Gate>} />

        {/* Piano Editoriale Digitale */}
        <Route path="ped" element={<Gate allowed={isComplete}><PEDPage /></Gate>} />

        {/* Campagne ADV — L3+ */}
        <Route path="adv" element={<Gate allowed={isComplete}><ProtectedRoute maxLevel={3}><ADVPage /></ProtectedRoute></Gate>} />

        {/* Crescita social a pagamento (panel SMM) */}
        <Route path="crescita-social" element={<Gate allowed={isComplete}><ProtectedRoute maxLevel={4}><CrescitaSocialPage /></ProtectedRoute></Gate>} />

        {/* Ufficio Stampa (PR) — L3+ */}
        <Route
          path="pr"
          element={
            <Gate allowed={isComplete}>
              <ProtectedRoute maxLevel={3}>
                <Suspense fallback={<PageLoader />}>
                  <PRPage />
                </Suspense>
              </ProtectedRoute>
            </Gate>
          }
        />

        {/* Branding — L3+ */}
        <Route
          path="branding"
          element={
            <Gate allowed={isComplete}>
              <ProtectedRoute maxLevel={3}>
                <Suspense fallback={<PageLoader />}>
                  <BrandingPage />
                </Suspense>
              </ProtectedRoute>
            </Gate>
          }
        />

        {/* Consulenza — L3+ */}
        <Route
          path="consulenza"
          element={
            <Gate allowed={isComplete}>
              <ProtectedRoute maxLevel={3}>
                <Suspense fallback={<PageLoader />}>
                  <ConsulenzaPage />
                </Suspense>
              </ProtectedRoute>
            </Gate>
          }
        />

        {/* Digital Hub (Ticketing) — L3+ */}
        <Route
          path="digital"
          element={
            <Gate allowed={isComplete}>
              <ProtectedRoute maxLevel={3}>
                <Suspense fallback={<PageLoader />}>
                  <DigitalHubPage />
                </Suspense>
              </ProtectedRoute>
            </Gate>
          }
        />

        {/* Preventivi — L3+ */}
        <Route
          path="preventivi"
          element={
            <Gate allowed={isComplete}>
              <ProtectedRoute maxLevel={3}>
                <Suspense fallback={<PageLoader />}>
                  <QuotesPage />
                </Suspense>
              </ProtectedRoute>
            </Gate>
          }
        />
        <Route
          path="preventivi/:id"
          element={
            <Gate allowed={isComplete}>
              <ProtectedRoute maxLevel={3}>
                <Suspense fallback={<PageLoader />}>
                  <QuoteDetailPage />
                </Suspense>
              </ProtectedRoute>
            </Gate>
          }
        />

        {/* Collaboratori esterni — gestione admin (L2+) */}
        <Route
          path="collaboratori-esterni"
          element={
            <Gate allowed={isComplete}>
              <ProtectedRoute maxLevel={2}>
                <Suspense fallback={<PageLoader />}>
                  <PartnerCollaboratorsPage />
                </Suspense>
              </ProtectedRoute>
            </Gate>
          }
        />

        {/* Contabilità (L2+) — sempre raggiungibile, anche con istanza non completa */}
        <Route
          path="accounting"
          element={
            <ProtectedRoute maxLevel={2}>
              <Suspense fallback={<PageLoader />}>
                <AccountingPage />
              </Suspense>
            </ProtectedRoute>
          }
        />

        {/* Team Workload (Manager L3+) */}
        <Route
          path="team"
          element={
            <Gate allowed={isComplete}>
              <ProtectedRoute maxLevel={3}>
                <Suspense fallback={<PageLoader />}>
                  <TeamWorkloadPage />
                </Suspense>
              </ProtectedRoute>
            </Gate>
          }
        />

        {/* Knowledge Base — L3+ */}
        <Route
          path="knowledge-base"
          element={
            <Gate allowed={isComplete}>
              <ProtectedRoute maxLevel={3}>
                <Suspense fallback={<PageLoader />}>
                  <KnowledgeBasePage />
                </Suspense>
              </ProtectedRoute>
            </Gate>
          }
        />

        {/* Email — sempre raggiungibile, anche con istanza non completa */}
        <Route
          path="email"
          element={
            <GuestRoute>
              <Suspense fallback={<PageLoader />}>
                <EmailPage />
              </Suspense>
            </GuestRoute>
          }
        />

        {/* Risorse (Abbonamenti) — L2+ */}
        <Route
          path="risorse"
          element={
            <Gate allowed={isComplete}>
              <ProtectedRoute maxLevel={2}>
                <Suspense fallback={<PageLoader />}>
                  <RisorsePage />
                </Suspense>
              </ProtectedRoute>
            </Gate>
          }
        />

        {/* Social Publisher — L3+ */}
        <Route
          path="social-publisher"
          element={
            <Gate allowed={isComplete}>
              <ProtectedRoute maxLevel={3}>
                <Suspense fallback={<PageLoader />}>
                  <SocialPublisherPage />
                </Suspense>
              </ProtectedRoute>
            </Gate>
          }
        />

        {/* Richieste Sito (Leads CRM) — L3+ */}
        <Route
          path="richieste"
          element={
            <Gate allowed={isComplete}>
              <ProtectedRoute maxLevel={3}>
                <Suspense fallback={<PageLoader />}>
                  <RichiestePage />
                </Suspense>
              </ProtectedRoute>
            </Gate>
          }
        />
        <Route
          path="richieste/:id"
          element={
            <Gate allowed={isComplete}>
              <ProtectedRoute maxLevel={3}>
                <Suspense fallback={<PageLoader />}>
                  <LeadDetailPage />
                </Suspense>
              </ProtectedRoute>
            </Gate>
          }
        />

        {/* Visual Production */}
        <Route
          path="visual-production"
          element={
            <Gate allowed={isComplete}>
              <GuestRoute>
                <Suspense fallback={<PageLoader />}>
                  <VisualProductionPage />
                </Suspense>
              </GuestRoute>
            </Gate>
          }
        />

        {/* Merchandising */}
        <Route
          path="merchandising"
          element={
            <Gate allowed={isComplete}>
              <GuestRoute>
                <Suspense fallback={<PageLoader />}>
                  <MerchandisingPage />
                </Suspense>
              </GuestRoute>
            </Gate>
          }
        />

        {/* Note personali */}
        <Route
          path="notes"
          element={
            <Gate allowed={isComplete}>
              <GuestRoute>
                <Suspense fallback={<PageLoader />}>
                  <NotesPage />
                </Suspense>
              </GuestRoute>
            </Gate>
          }
        />

        {/* To-Do personale */}
        <Route
          path="todo"
          element={
            <Gate allowed={isComplete}>
              <GuestRoute>
                <Suspense fallback={<PageLoader />}>
                  <TodoPage />
                </Suspense>
              </GuestRoute>
            </Gate>
          }
        />

        {/* Magazzino Attrezzature */}
        <Route
          path="magazzino"
          element={
            <Gate allowed={isComplete}>
              <GuestRoute>
                <Suspense fallback={<PageLoader />}>
                  <WarehousePage />
                </Suspense>
              </GuestRoute>
            </Gate>
          }
        />

        {/* Assistente AI */}
        <Route path="ai" element={<Gate allowed={isComplete}><GuestRoute><AIAssistantPage /></GuestRoute></Gate>} />

        {/* Report & Analytics (L3+) */}
        <Route path="reports" element={<Gate allowed={isComplete}><ProtectedRoute maxLevel={3}><ReportsPage /></ProtectedRoute></Gate>} />

        {/* Notifiche — restano sempre raggiungibili */}
        <Route path="notifications" element={<NotificationsPage />} />

        {/* Impostazioni — sempre raggiungibili, anche con istanza non completa */}
        <Route path="settings" element={<GuestRoute><SettingsPage /></GuestRoute>} />
        <Route path="settings/users" element={<ProtectedRoute maxLevel={2}><UsersAdminPage /></ProtectedRoute>} />
        <Route path="settings/audit" element={<Gate allowed={isComplete}><ProtectedRoute maxLevel={2}><AuditTrailPage /></ProtectedRoute></Gate>} />
        <Route
          path="settings/catalog"
          element={
            <Gate allowed={isComplete}>
              <ProtectedRoute maxLevel={2}>
                <Suspense fallback={<PageLoader />}>
                  <CatalogAdminPage />
                </Suspense>
              </ProtectedRoute>
            </Gate>
          }
        />
        <Route
          path="suppliers"
          element={
            <Gate allowed={isComplete}>
              <ProtectedRoute maxLevel={2}>
                <Suspense fallback={<PageLoader />}>
                  <SuppliersPage />
                </Suspense>
              </ProtectedRoute>
            </Gate>
          }
        />

        {/* Review Contenuti (tutti i livelli, incluso guest) */}
        <Route
          path="content-review"
          element={
            <Gate allowed={isComplete}>
              <ProtectedRoute>
                <Suspense fallback={<PageLoader />}>
                  <ContentReviewPage />
                </Suspense>
              </ProtectedRoute>
            </Gate>
          }
        />

        {/* Modifiche in attesa di approvazione */}
        <Route
          path="pending-changes"
          element={
            <Gate allowed={isComplete}>
              <GuestRoute>
                <Suspense fallback={<PageLoader />}>
                  <PendingChangesPage />
                </Suspense>
              </GuestRoute>
            </Gate>
          }
        />

        {/* Accessi Guest — L2+ */}
        <Route
          path="settings/guests"
          element={
            <Gate allowed={isComplete}>
              <ProtectedRoute maxLevel={2}>
                <Suspense fallback={<PageLoader />}>
                  <GuestsAdminPage />
                </Suspense>
              </ProtectedRoute>
            </Gate>
          }
        />

        {/* Salute AI — telemetria assistente, solo admin (L2+) */}
        <Route
          path="settings/ai-health"
          element={
            <Gate allowed={isComplete}>
              <ProtectedRoute maxLevel={2}>
                <Suspense fallback={<PageLoader />}>
                  <AIHealthPage />
                </Suspense>
              </ProtectedRoute>
            </Gate>
          }
        />

        {/* Collega Telegram — ogni utente collega il PROPRIO account. GuestRoute
            esclude i clienti-guest, come per l'assistente AI (/ai). */}
        <Route
          path="settings/telegram"
          element={
            <Gate allowed={isComplete}>
              <ProtectedRoute>
                <GuestRoute>
                  <Suspense fallback={<PageLoader />}>
                    <TelegramLinkPage />
                  </Suspense>
                </GuestRoute>
              </ProtectedRoute>
            </Gate>
          }
        />

        {/* Fallback - redirect alla dashboard (o al primo modulo attivo) */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Route>
    </Routes>
  )
}
